import { NUM_FILTER_SLOTS } from '../../openlayers/shader'
import {
  type DataFilterConfig,
  type LassoMode,
  type LassoSelection,
  MapMode,
  type PassEditSession,
  useMapStateStore
} from '../core'

/**
 * Stable category key for the "pass number" layer. Distinct from `Layer.id`
 * (which can vary across streams). Pass rows in the filter editor swap their
 * checkbox UI for a click-to-select map interaction — see {@link PassEditSession}.
 */
export const PASS_LAYER_KEY = 'pass'

export const useLassoFilters = () => {
  return useMapStateStore((store) => store.dataTools.filters)
}

/**
 * Strip placeholder sentinels from a filter list before sending to the backend.
 * The editor uses `{ blue: -1 }` for discrete layers the user has added but not
 * yet picked any blues for, and `{ min: 0, max: 0 }` for continuous layers
 * before their legend resolves. Both are inert client-side (the shader skips
 * them) but the backend ANDs include entries together, so any sentinel makes
 * the entire request match nothing.
 */
export const effectiveFilters = (filters: DataFilterConfig[]): DataFilterConfig[] =>
  filters.filter((f) => {
    if (f.disabled === true) return false
    if ('blue' in f.range) return f.range.blue >= 0
    return !(f.range.bounds.minimum === 0 && f.range.bounds.maximum === 0)
  })

/**
 * Synchronous read of the data-tools state. Used by non-React consumers
 * (DataLayerManager, etc.) that need the current filters at the moment of a
 * tile rebuild without going through a hook.
 */
export const getDataTools = () => useMapStateStore.getState().dataTools

/**
 * Subscribe to filter-list changes outside React. Fires on every change of
 * `dataTools.filters` (additions, removals, value tweaks). Returns the
 * unsubscribe function — caller is responsible for invoking it on teardown.
 *
 * The shader-uniform pipeline uses this to push slider/checkbox tweaks to
 * existing tiles without rebuilding the WebGLTile, so the map stays smooth
 * during interactive edits.
 */
export const onFilterChange = (cb: (filters: DataFilterConfig[]) => void): (() => void) =>
  useMapStateStore.subscribe(
    (state) => state.dataTools.filters,
    (filters) => cb(filters)
  )

/**
 * Composite key identifying one layer in a filter list. Reused by anything
 * that groups filters by layer — picker, editor, shader-uniform pipeline.
 */
export const filterLayerKey = (
  f: Pick<DataFilterConfig, 'streamId' | 'applicationId' | 'layerId'>
): string => `${f.streamId}|${f.applicationId}|${f.layerId}`

const distinctLayerCount = (filters: DataFilterConfig[]): number =>
  new Set(filters.map(filterLayerKey)).size

/**
 * Sets the full filter list, enforcing the {@link NUM_FILTER_SLOTS} cap on
 * distinct layers (multiple discrete-blue entries for the same layer count
 * as one). Filters past the cap are dropped silently.
 */
export const setLassoFilters = (filters: DataFilterConfig[]) => {
  useMapStateStore.setState((state) => ({
    dataTools: { ...state.dataTools, filters: applyCap(filters) }
  }))
}

const applyCap = (filters: DataFilterConfig[]): DataFilterConfig[] => {
  const accepted: DataFilterConfig[] = []
  const seen = new Set<string>()
  for (const f of filters) {
    const key = filterLayerKey(f)
    if (seen.has(key)) {
      accepted.push(f)
      continue
    }
    if (seen.size >= NUM_FILTER_SLOTS) continue
    seen.add(key)
    accepted.push(f)
  }
  return accepted
}

/** Append a filter entry. Rejected silently if it would exceed the layer cap. */
export const appendFilter = (filter: DataFilterConfig) => {
  useMapStateStore.setState((state) => {
    const existing = state.dataTools.filters
    const key = filterLayerKey(filter)
    const alreadyHasLayer = existing.some((f) => filterLayerKey(f) === key)
    if (!alreadyHasLayer && distinctLayerCount(existing) >= NUM_FILTER_SLOTS) {
      return state
    }
    return { dataTools: { ...state.dataTools, filters: [...existing, filter] } }
  })
}

/**
 * Remove every entry for a given layer. Used by the Remove × button. Also
 * clears the pass-edit session if the removed layer happens to be the armed
 * row, so editing state can never outlive its underlying filter row.
 */
export const removeFiltersForLayer = (
  layer: Pick<DataFilterConfig, 'streamId' | 'applicationId' | 'layerId'>
) => {
  const target = filterLayerKey(layer)
  useMapStateStore.setState((state) => {
    const { passEdit } = state.dataTools
    const passEditNext = passEdit !== null && filterLayerKey(passEdit) === target ? null : passEdit
    return {
      dataTools: {
        ...state.dataTools,
        filters: state.dataTools.filters.filter((f) => filterLayerKey(f) !== target),
        passEdit: passEditNext
      }
    }
  })
}

/**
 * Replace every entry matching `predicate` with `next`. Current callers pass
 * predicates that narrow to a single entry per layer (e.g. `'bounds' in
 * f.range`), so behaviorally this acts as a single replace — but a coarser
 * predicate would overwrite all matches with the same value. Pick the
 * predicate accordingly.
 */
export const replaceFilter = (
  predicate: (f: DataFilterConfig) => boolean,
  next: DataFilterConfig
) => {
  useMapStateStore.setState((state) => ({
    dataTools: {
      ...state.dataTools,
      filters: state.dataTools.filters.map((f) => (predicate(f) ? next : f))
    }
  }))
}

/** Remove every entry matching `predicate`. Used by the discrete checklist. */
export const removeFilterEntries = (predicate: (f: DataFilterConfig) => boolean) => {
  useMapStateStore.setState((state) => ({
    dataTools: {
      ...state.dataTools,
      filters: state.dataTools.filters.filter((f) => !predicate(f))
    }
  }))
}

/**
 * Flip every entry on a given layer between include and exclude in lockstep.
 * Also propagates to a matching pass-edit session so the preview channel and
 * commit path can't disagree with `filters[].type` mid-edit. Clears any
 * `disabled` flag so the row re-enters the active set — picking a mode is the
 * user re-engaging the filter.
 */
export const setFilterType = (
  layer: Pick<DataFilterConfig, 'streamId' | 'applicationId' | 'layerId'>,
  type: 'include' | 'exclude'
) => {
  const target = filterLayerKey(layer)
  useMapStateStore.setState((state) => {
    const { passEdit } = state.dataTools
    const passEditNext =
      passEdit !== null && filterLayerKey(passEdit) === target ? { ...passEdit, type } : passEdit
    return {
      dataTools: {
        ...state.dataTools,
        filters: state.dataTools.filters.map((f) =>
          filterLayerKey(f) === target ? { ...f, type, disabled: false } : f
        ),
        passEdit: passEditNext
      }
    }
  })
}

/**
 * Toggle a whole layer's `disabled` flag. Disabled filters are preserved in
 * state — their selections and range survive — but are skipped by both the
 * shader (`applyAllSlotUniforms`) and the backend (`effectiveFilters`), so the
 * map and any analysis behave as if the row were absent. Also disarms a
 * matching pass-edit session so a row can't be edited while inactive.
 */
export const setFilterDisabled = (
  layer: Pick<DataFilterConfig, 'streamId' | 'applicationId' | 'layerId'>,
  disabled: boolean
) => {
  const target = filterLayerKey(layer)
  useMapStateStore.setState((state) => {
    const { passEdit } = state.dataTools
    const passEditNext =
      disabled && passEdit !== null && filterLayerKey(passEdit) === target ? null : passEdit
    return {
      dataTools: {
        ...state.dataTools,
        filters: state.dataTools.filters.map((f) =>
          filterLayerKey(f) === target ? { ...f, disabled } : f
        ),
        passEdit: passEditNext
      }
    }
  })
}

export const useIsLassoActive = () =>
  useMapStateStore((s) => s.dataTools.lassoActive || s.mode === MapMode.Lasso)

export const toggleLasso = (enabled: boolean) => {
  useMapStateStore.setState((state) => {
    if (state.dataTools.lassoActive === enabled) return state
    return { dataTools: { ...state.dataTools, lassoActive: enabled } }
  })
}

export const useLassoMode = () => useMapStateStore((s) => s.dataTools.lassoMode)

/**
 * Also re-activates the lasso (clears disable), like {@link setFilterType}.
 */
export const setLassoMode = (mode: LassoMode) => {
  useMapStateStore.setState((state) => {
    if (state.dataTools.lassoMode === mode && state.dataTools.lassoActive) return state
    return { dataTools: { ...state.dataTools, lassoMode: mode, lassoActive: true } }
  })
}

/**
 * Subscribe to lasso-mode changes outside React. Returns the unsubscribe.
 */
export const observeLassoMode = (cb: (mode: LassoMode) => void): (() => void) =>
  useMapStateStore.subscribe((state) => state.dataTools.lassoMode, cb)

export const setLassoDrawing = (drawing: boolean) => {
  useMapStateStore.setState((state) => {
    if (state.dataTools.lassoDrawing === drawing) return state
    return { dataTools: { ...state.dataTools, lassoDrawing: drawing } }
  })
}

/** The drawn lasso region, gated by whether lasso is active on either platform. */
export const useActiveLassoState = () =>
  useMapStateStore((s) =>
    s.dataTools.lassoActive || s.mode === MapMode.Lasso ? s.dataTools.lasso : undefined
  )

export const setLassoState = (lasso: LassoSelection) => {
  useMapStateStore.setState((state) => ({
    dataTools: { ...state.dataTools, lasso }
  }))
}

export const clearLassoCollection = () => {
  useMapStateStore.setState((state) => {
    if (state.dataTools.lasso === undefined) return state
    return { dataTools: { ...state.dataTools, lasso: undefined } }
  })
}

/**
 * Arm a pass-row edit session. Replaces any existing session — only one row
 * can be armed at a time. `initialBlues` should be the row's currently
 * committed pass blues so Cancel can revert without loss.
 */
export const armPassEdit = (
  args: Omit<PassEditSession, 'workingBlues' | 'hoveredBlue'> & { initialBlues: number[] }
) => {
  const { initialBlues, ...rest } = args
  useMapStateStore.setState((state) => ({
    dataTools: {
      ...state.dataTools,
      passEdit: { ...rest, workingBlues: [...initialBlues], hoveredBlue: null }
    }
  }))
}

/** Disarm without committing — used by Cancel and tab-away cleanup. */
export const disarmPassEdit = () => {
  useMapStateStore.setState((state) => {
    if (state.dataTools.passEdit === null) return state
    return { dataTools: { ...state.dataTools, passEdit: null } }
  })
}

/** Toggle a pass blue in the working selection. No-op when not armed. */
export const togglePassEditBlue = (blue: number) => {
  useMapStateStore.setState((state) => {
    const { passEdit } = state.dataTools
    if (passEdit === null) return state
    const has = passEdit.workingBlues.includes(blue)
    const workingBlues = has
      ? passEdit.workingBlues.filter((b) => b !== blue)
      : [...passEdit.workingBlues, blue]
    return { dataTools: { ...state.dataTools, passEdit: { ...passEdit, workingBlues } } }
  })
}

/** Set the cursor's current pass blue (or null when off-map / over no data). */
export const setPassEditHover = (blue: number | null) => {
  useMapStateStore.setState((state) => {
    const { passEdit } = state.dataTools
    if (passEdit === null) return state
    if (passEdit.hoveredBlue === blue) return state
    return { dataTools: { ...state.dataTools, passEdit: { ...passEdit, hoveredBlue: blue } } }
  })
}

/**
 * Commit the working selection into `filters` and disarm. Strips every
 * existing entry for the armed layer and replaces them with one entry per
 * working blue (or a single `{blue:-1}` placeholder if the working set is
 * empty, so the row stays visible in the editor — same sentinel discipline
 * as the AddFilterModal's commit path).
 */
export const commitPassEdit = () => {
  useMapStateStore.setState((state) => {
    const { passEdit, filters } = state.dataTools
    if (passEdit === null) return state
    const target = filterLayerKey(passEdit)
    const others = filters.filter((f) => filterLayerKey(f) !== target)
    const baseFilter = {
      streamId: passEdit.streamId,
      applicationId: passEdit.applicationId,
      layerId: passEdit.layerId,
      type: passEdit.type
    }
    const additions: DataFilterConfig[] =
      passEdit.workingBlues.length === 0
        ? [{ ...baseFilter, range: { blue: -1 } }]
        : passEdit.workingBlues.map((blue) => ({ ...baseFilter, range: { blue } }))
    return {
      dataTools: { ...state.dataTools, filters: [...others, ...additions], passEdit: null }
    }
  })
}

export const usePassEditState = () => useMapStateStore((s) => s.dataTools.passEdit)

export const useIsPassRowArmed = (layerKey: string) =>
  useMapStateStore(
    (s) => s.dataTools.passEdit !== null && filterLayerKey(s.dataTools.passEdit) === layerKey
  )

/**
 * Subscribe to the editing session's preview shape — the union of working
 * blues and the hovered blue, materialized as `DataFilterConfig` entries the
 * shader pipeline can OR onto its slot uniforms. Emits `[]` when no row is
 * armed. Mirrors the API shape of {@link onFilterChange} for non-React
 * consumers (DataLayerManager).
 */
export const onPassEditChange = (cb: (preview: DataFilterConfig[]) => void): (() => void) =>
  useMapStateStore.subscribe(
    (state) => state.dataTools.passEdit,
    (passEdit) => cb(passEditPreview(passEdit))
  )

/** Materialize an edit session into preview filter entries. */
export const passEditPreview = (passEdit: PassEditSession | null): DataFilterConfig[] => {
  if (passEdit === null) return []
  const blues = new Set(passEdit.workingBlues)
  if (passEdit.hoveredBlue !== null) blues.add(passEdit.hoveredBlue)
  if (blues.size === 0) return []
  const base = {
    streamId: passEdit.streamId,
    applicationId: passEdit.applicationId,
    layerId: passEdit.layerId,
    type: passEdit.type
  }
  return [...blues].map((blue) => ({ ...base, range: { blue } }))
}
